import React, { useCallback, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { getUserL2Access } from 'Deer/helpers/getUserAccess';
import { CollapseContainer } from 'components/RouteMatchSidebarNavContainer/components/CollapseContainer';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { setSelectedReviewPeriodSummarySection, useSelectedReviewPeriodSummarySection } from 'reducers/reviewFormSlice';
import { REVIEW_SUMMARY_SECTIONS } from 'screens/performanceReview/FormsPageRabbit';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import useReviewFormSidebarQueryData from 'screens/performanceReview/hooks/useReviewFormSidebarQueryData';
import { checkFeedbackModuleEnabled, checkGoalModuleEnabled, checkTaskModuleEnabled } from 'utils/helpers/moduleAccess';

import {
  FeedbackIconDualTone,
  FeedbackIconHover,
  FeedbackIconSelected,
  GoalIconDualTone,
  GoalIconHover,
  GoalIconSelected,
  InitiativesIconDualTone,
  InitiativesIconHover,
  InitiativesIconSelected,
} from 'assets/assetsV1/iconComponents';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { findAndReplaceString } from 'constants/helper';
import { sendClickedReviewContextEvent } from 'constants/mixpanelHelpers/reviewsEvents';

import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TextLabel } from 'uiToolkitV1/Typography';

import DividerForPanel from './common/DividerForPanel';

const ListItemLeftComp = (IconComp) => () => <IconComp boxSize="24px" fill="transparent" />;

const DualToneIconNavItem = ({
  id,
  title,
  subTitle,
  Icon = null,
  HoverIcon = null,
  ActiveIcon = null,
  isActive = false,
  isClickable = true,
  empId,
}) => {
  const dispatch = useDispatch();
  const { moduleName, objectivesName } = useOrganisationSettings();

  const [hovering, setHovering] = useState(false);
  const [mouseDownClicking, setMouseDownClicking] = useState(false);
  const onMouseEnterHandler = useCallback(() => {
    if (!isClickable) return;
    setHovering(true);
  }, []);
  const onMouseLeaveHandler = useCallback(() => {
    if (!isClickable) return;
    setHovering(false);
  }, []);
  const onMouseDownHandler = useCallback(() => {
    if (!isClickable) return;
    setMouseDownClicking(true);
  }, []);
  const onMouseUpHandler = useCallback(() => {
    if (!isClickable) return;
    setMouseDownClicking(false);
  }, []);

  let IconComp = () => null;

  if (Icon) {
    IconComp = Icon;
  }

  if (hovering && !!HoverIcon && !isActive) {
    IconComp = HoverIcon;
  }

  if ((isActive && !!ActiveIcon) || mouseDownClicking) {
    IconComp = ActiveIcon;
  }

  const clickHandler = () => {
    if (!isClickable) return;
    sendClickedReviewContextEvent({
      eventProperties: {
        empId,
        type: id,
      },
    });
    dispatch(setSelectedReviewPeriodSummarySection({ id }));
  };

  const updatedTitle = findAndReplaceString(title, modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE), 'Goals');
  const updatedSubTitle = findAndReplaceString(
    subTitle,
    modifyString(objectivesName, StringModifier.PLURAL_SENTENCE_CASE),
    'goals'
  );

  return (
    <Box
      tabIndex={0}
      role="button"
      onMouseEnter={onMouseEnterHandler}
      onMouseLeave={onMouseLeaveHandler}
      onMouseDown={onMouseDownHandler}
      onMouseUp={onMouseUpHandler}
      mb={1}
    >
      <ListItemComp
        LeftComponent={ListItemLeftComp(IconComp)}
        active={isActive}
        title={updatedTitle === 'Goals' ? moduleName : updatedTitle}
        subTitle={updatedSubTitle}
        padding="4px"
        isBoldText={isActive}
        onClick={clickHandler}
      />
    </Box>
  );
};

const getInfoItemList = ({ stats }) => {
  if (!stats) return [];

  const { goalSummaryStats = {}, taskStats = {}, meshStrengthStats = {} } = stats;
  const goalItem = {
    id: REVIEW_SUMMARY_SECTIONS.GOALS.id,
    title: 'Goals',
    subTitle: `${Math.round(Number.parseFloat(goalSummaryStats.actual))} % • ${goalSummaryStats.totalCount} goals`,
    Icon: GoalIconDualTone,
    HoverIcon: GoalIconHover,
    ActiveIcon: GoalIconSelected,
    isClickable: true,
  };
  const taskItem = {
    id: REVIEW_SUMMARY_SECTIONS.INITIATIVES.id,
    title: 'Initiatives',
    subTitle: `${taskStats.completed} On time • ${taskStats.delayed} Delayed`,
    Icon: InitiativesIconDualTone,
    HoverIcon: InitiativesIconHover,
    ActiveIcon: InitiativesIconSelected,
    isClickable: true,
  };

  const strengthItem = {
    id: REVIEW_SUMMARY_SECTIONS.FEEDBACK.id,
    title: 'Feedback',
    subTitle: `${meshStrengthStats.appreciativeFeedbackCount} ${
      meshStrengthStats.appreciativeFeedbackCount <= 1 ? 'Praise' : 'Praises'
    } • ${meshStrengthStats.constructiveFeedbackCount} ${
      meshStrengthStats.constructiveFeedbackCount <= 1 ? 'Advice' : 'Advices'
    }`,
    Icon: FeedbackIconDualTone,
    HoverIcon: FeedbackIconHover,
    ActiveIcon: FeedbackIconSelected,
    isClickable: true,
  };

  const getFinalList = () => {
    let list = [goalItem, taskItem, strengthItem];
    const { hasFeedbackAccess, hasIDPAccess } = getUserL2Access();
    // filter to remove disable features
    if (!checkGoalModuleEnabled()) {
      list = list.filter(({ id }) => id !== 'GOALS');
    }
    if (!checkTaskModuleEnabled()) {
      list = list.filter(({ id }) => id !== 'INITIATIVES');
    }
    if (!checkFeedbackModuleEnabled() && !hasFeedbackAccess && !hasIDPAccess) {
      list = list.filter(({ id }) => !['FEEDBACK'].includes(id));
    }
    return list;
  };

  return getFinalList();
};

const SidebarReviewPeriodSummary = () => {
  const data = useReviewFormSidebarQueryData();
  const { employeeId } = useReviewFormParams();

  const selectedReviewPeriodSummarySection = useSelectedReviewPeriodSummarySection();

  const infoItemList = getInfoItemList({
    stats: data,
  });

  if (!infoItemList.length) return null;

  return (
    <>
      <DividerForPanel />
      <Box>
        <TextLabel bold px="1" py={1} mb={2} TextLabel color="text.g2">
          REVIEW CONTEXT
        </TextLabel>
        <Box mb={1}>
          <CollapseContainer>
            {infoItemList.map(({ id, ...props }) => (
              <DualToneIconNavItem
                {...props}
                id={id}
                isActive={id === selectedReviewPeriodSummarySection}
                key={id}
                empId={employeeId}
              />
            ))}
          </CollapseContainer>
        </Box>
      </Box>
    </>
  );
};

export default SidebarReviewPeriodSummary;
